import { z } from 'zod'
import { NON_EU_COUNTRIES_WITHOUT_POSTCODES } from '@/common'

export type SupplierFormData = {
  isBusinessRegistrationNumberNotKnown: boolean
  supplierName: string
  businessRegistrationNumber: string
  street: string
  streetNumber: string
  street2: string
  postCode: string
  city: string
  poBox: string
  subDivision: string
  country: string
  contactName: string
  contactPhoneNumber: string
  contactEmail: string
}

export const supplierDetailsSchema = z
  .object({
    supplierName: z.string().min(1),
    businessRegistrationNumber: z.string().optional(),
    isBusinessRegistrationNumberNotKnown: z.boolean()
  })
  .refine(
    data => {
      // If business registration number is not marked as unknown, it must be provided
      if (!data.isBusinessRegistrationNumberNotKnown) {
        return data.businessRegistrationNumber && data.businessRegistrationNumber.length > 0
      }
      return true
    },
    {
      path: ['businessRegistrationNumber']
    }
  )

export const supplierAddressSchema = z
  .object({
    street: z.string().max(70).optional(),
    streetNumber: z.string().max(35).optional(),
    street2: z.string().max(70).optional(),
    postCode: z.string().max(17).optional(),
    city: z.string().min(2).max(35),
    poBox: z.string().max(70).optional(),
    subDivision: z.string().max(35).optional(),
    country: z.string().length(2)
  })
  .refine(
    data => {
      const doesCountryHavePostCode = !NON_EU_COUNTRIES_WITHOUT_POSTCODES.includes(data.country)

      if (doesCountryHavePostCode && !data.postCode) {
        return false
      }
      return true
    },
    {
      error: 'Post code is required for countries with post codes',
      path: ['postCode']
    }
  )

export const supplierContactSchema = z.object({
  contactName: z.string().min(1),
  contactPhoneNumber: z.string().min(1),
  contactEmail: z.email().min(1)
})

const supplierFormSchema = z
  .object({
    isBusinessRegistrationNumberNotKnown: z.boolean(),
    supplierName: z.string().min(1),
    businessRegistrationNumber: z.string().optional(),
    street: z.string().optional(),
    streetNumber: z.string().optional(),
    street2: z.string().optional(),
    postCode: z.string().optional(),
    city: z.string().min(1),
    poBox: z.string().optional(),
    subDivision: z.string().optional(),
    country: z.string().min(1),
    contactName: z.string().min(1),
    contactPhoneNumber: z.string().min(1),
    contactEmail: z.email().min(1)
  })
  .refine(
    data => {
      // If business registration number is not marked as unknown, it must be provided
      if (!data.isBusinessRegistrationNumberNotKnown) {
        return data.businessRegistrationNumber && data.businessRegistrationNumber.length > 0
      }
      return true
    },
    {
      path: ['businessRegistrationNumber']
    }
  )
  .refine(
    data => {
      const doesCountryHavePostCode = !NON_EU_COUNTRIES_WITHOUT_POSTCODES.includes(data.country)

      if (doesCountryHavePostCode && !data.postCode) {
        return false
      }
      return true
    },
    {
      error: 'Post code is required for countries with post codes',
      path: ['postCode']
    }
  )

export const validateSupplierForm = (data: SupplierFormData) => {
  return supplierFormSchema.safeParse(data)
}
